// src/components/dashboard/ViewRegistrationsModal.js
// Updated: 06/28/2026

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import { FaTimes, FaSearch, FaDownload, FaCheckCircle, FaBoxOpen, FaStickyNote, FaUserCircle, FaQrcode } from 'react-icons/fa';
import Spinner from '../common/Spinner';
import { Scanner } from '@yudiel/react-qr-scanner'; 

const ViewRegistrationsModal = ({ familyId, event, onClose }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search & Scanner State
  const [searchTerm, setSearchTerm] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [expandedRegId, setExpandedRegId] = useState(null);

  useEffect(() => {
    if (!event || !familyId) return;
    const q = collection(db, `families/${familyId}/events/${event.id}/registrations`);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRegistrations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching registrations:", error);
      toast.error("Failed to load registrations.");
      setLoading(false);
    });
    return () => unsubscribe();
  }, [event, familyId]);

  // Handle Check-in, Items, and Notes Updates
  const handleAttendeeUpdate = async (regId, attendeeId, field, value) => {
    const regToUpdate = registrations.find(r => r.id === regId);
    if (!regToUpdate) return;

    const updatedAttendees = regToUpdate.attendees.map(att => 
        att.id === attendeeId ? { ...att, [field]: value } : att
    );

    try {
        await updateDoc(doc(db, `families/${familyId}/events/${event.id}/registrations/${regId}`), {
            attendees: updatedAttendees
        });
        toast.success("Updated successfully", { id: 'update-toast' });
    } catch (error) {
        console.error("Error updating attendee:", error);
        toast.error("Failed to update.");
    }
  };

  // --- FIXED: Camera Scan Handler ---
  const handleScan = (scannedText) => {
    if (scannedText) {
        const cleanText = scannedText.trim(); // Ensure no invisible spaces break the search
        setSearchTerm(cleanText);
        setIsScanning(false); // Close the camera
        
        // Check if the scanned ID matches a registration
        const foundReg = registrations.find(r => r.id === cleanText);
        if (foundReg) {
            setExpandedRegId(foundReg.id);
            toast.success("Ticket Found!");
        } else {
            toast.error("Ticket ID not found.");
        }
    }
  };

  // Prepare Flattened Data for CSV Export
  const csvData = useMemo(() => {
    const rows = [];
    registrations.forEach(reg => {
      reg.attendees.forEach(att => {
        rows.push({
          "Ticket ID": reg.id,
          "Attendee Name": att.name,
          "Age": att.age,
          "T-Shirt Name": att.tshirtName || 'None',
          "T-Shirt Size": att.tshirtSize || 'None',
          "Total Cost (Per Person)": att.cost,
          "Payment Status": reg.paymentStatus,
          "Checked In": att.checkedIn ? "Yes" : "No",
          "Items Received": att.itemsReceived ? "Yes" : "No",
          "Host Notes": att.hostNotes || ''
        });
      });
    });
    return rows;
  }, [registrations]);

  // Filter registrations by Search (Name or Ticket ID)
  const filteredRegistrations = useMemo(() => {
      if (!searchTerm) return registrations;
      const lowerSearch = searchTerm.toLowerCase();
      return registrations.filter(reg => 
          reg.id.toLowerCase().includes(lowerSearch) || 
          reg.attendees.some(att => att.name.toLowerCase().includes(lowerSearch))
      );
  }, [registrations, searchTerm]);

  // Manual Search Submit
  const handleSearchSubmit = (e) => {
      e.preventDefault();
      if (filteredRegistrations.length === 1) {
          setExpandedRegId(filteredRegistrations[0].id);
      }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50 overflow-hidden">
      <div className="bg-gray-50 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-white rounded-t-lg flex-wrap gap-4">
          <div>
              <h2 className="text-2xl font-semibold text-neutral-gray">{event.eventName} Check-in</h2>
              <p className="text-sm text-gray-500 mt-1">{registrations.reduce((acc, curr) => acc + curr.attendees.length, 0)} total attendees across {registrations.length} registrations.</p>
          </div>
          <div className="flex items-center gap-4">
              <CSVLink 
                  data={csvData} 
                  filename={`${event.eventName.replace(/\s+/g, '_')}_Registrations.csv`}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm transition-colors shadow-sm"
              >
                  <FaDownload /> Export CSV
              </CSVLink>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FaTimes size={24} /></button>
          </div>
        </div>

        {/* Search Bar / Camera Scanner Controls */}
        <div className="p-4 bg-white border-b shadow-sm z-10">
            <form onSubmit={handleSearchSubmit} className="max-w-xl mx-auto flex gap-2">
                <div className="relative flex-grow">
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Search by Name or type Ticket ID..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary shadow-sm"
                    />
                </div>
                {/* Toggle Camera Button */}
                <button 
                    type="button" 
                    onClick={() => setIsScanning(!isScanning)} 
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-white transition-colors ${isScanning ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-blue-700'}`}
                >
                    <FaQrcode /> {isScanning ? 'Close Camera' : 'Scan'}
                </button>
            </form>

            {/* FIXED: Updated Scanner syntax for the latest version */}
            {isScanning && (
                <div className="mt-4 max-w-sm mx-auto overflow-hidden rounded-lg shadow-lg border-2 border-primary bg-black relative">
                    <Scanner 
                        onScan={(result) => {
                            if (result && result.length > 0) {
                                handleScan(result[0].rawValue);
                            }
                        }}
                        onError={(error) => console.log(error?.message)} 
                    />
                    <div className="bg-gray-800 text-white text-center text-xs py-2 absolute bottom-0 w-full">
                        Point camera at a digital ticket QR Code
                    </div>
                </div>
            )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : filteredRegistrations.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No registrations found.</p>
          ) : (
            filteredRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Accordion Header */}
                <div 
                    className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setExpandedRegId(expandedRegId === reg.id ? null : reg.id)}
                >
                    <div>
                        <h3 className="font-bold text-lg text-primary">{reg.attendees[0]?.name} <span className="text-gray-500 font-normal text-sm ml-2">+{reg.attendees.length - 1} more</span></h3>
                        <p className="text-xs font-mono text-gray-400 mt-1">Ticket ID: {reg.id}</p>
                    </div>
                    <div className="text-right">
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${reg.paymentStatus === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {reg.paymentStatus}
                        </span>
                        <p className="font-semibold text-gray-700 mt-1">${reg.totalCost.toFixed(2)}</p>
                    </div>
                </div>

                {/* Accordion Body (Attendees List) */}
                {expandedRegId === reg.id && (
                    <div className="divide-y divide-gray-100">
                        {reg.attendees.map(att => (
                            <div key={att.id} className="p-4 flex flex-col md:flex-row gap-6 hover:bg-blue-50/30 transition-colors">
                                
                                {/* Info Column */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FaUserCircle className="text-gray-300 text-2xl"/>
                                        <h4 className="font-bold text-gray-800">{att.name} <span className="text-gray-400 font-normal text-sm">(Age {att.age})</span></h4>
                                    </div>
                                    <div className="ml-8 text-sm text-gray-600 space-y-1">
                                        {att.tshirtName ? <p>👕 {att.tshirtName} - Size {att.tshirtSize}</p> : <p className="text-gray-400">👕 No Merch</p>}
                                    </div>
                                </div>

                                {/* Check-In & Toggles Column */}
                                <div className="flex flex-col gap-3 justify-center md:w-48 border-l pl-6">
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input 
                                            type="checkbox" 
                                            checked={att.checkedIn || false} 
                                            onChange={(e) => handleAttendeeUpdate(reg.id, att.id, 'checkedIn', e.target.checked)}
                                            className="w-5 h-5 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                                        />
                                        <span className={`text-sm font-medium ${att.checkedIn ? 'text-green-600' : 'text-gray-600 group-hover:text-gray-800'}`}>
                                            <FaCheckCircle className="inline mr-1 mb-0.5"/> Checked In
                                        </span>
                                    </label>
                                    
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input 
                                            type="checkbox" 
                                            checked={att.itemsReceived || false} 
                                            onChange={(e) => handleAttendeeUpdate(reg.id, att.id, 'itemsReceived', e.target.checked)}
                                            className="w-5 h-5 rounded text-cta-orange focus:ring-cta-orange border-gray-300 cursor-pointer"
                                        />
                                        <span className={`text-sm font-medium ${att.itemsReceived ? 'text-cta-orange' : 'text-gray-600 group-hover:text-gray-800'}`}>
                                            <FaBoxOpen className="inline mr-1 mb-0.5"/> Items Given
                                        </span>
                                    </label>
                                </div>

                                {/* Host Notes Column */}
                                <div className="flex-1 md:max-w-xs relative">
                                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide"><FaStickyNote className="inline"/> Host Notes (Private)</label>
                                    <textarea
                                        value={att.hostNotes || ''}
                                        onChange={(e) => handleAttendeeUpdate(reg.id, att.id, 'hostNotes', e.target.value)}
                                        placeholder="Dietary needs, VIP, etc..."
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-sm p-2 resize-none bg-yellow-50/50"
                                        rows="3"
                                    />
                                </div>

                            </div>
                        ))}
                    </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewRegistrationsModal;